import { useEffect, useState } from "react";
import { Main } from "@/components/Main";
import { ClientSelection } from "./components/ClientSelection";
import { OrderSummary } from "./components/OrderSummary";
import { PaymentDeliverySelection } from "./components/PaymentDeliverySelection";
import { ProductSelection, SellerContestSummaryItem } from "./components/ProductSelection";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { createSale, SalesProps } from "@/Api/sales";
import { getPaymentFormsForSale } from "@/Api/sales";
import {
  getSellerBalances,
  getSellerContestsSummary,
  distributeDirectlyToSellers,
} from "@/Api/distributions";
import { getSelectSaleTypes } from "@/Api/saleTypes";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/auth";
import { Stepper } from "@/components/ui/stepper";
import { getCircuitRoutesData } from "@/Api/circuit_routes";
import { PAYMENT_FORMS, SALE_TYPE_ID } from "@/utils/constants";
import { Mask } from "@/utils/mask";
import { getSelectAllClientsAndLeads } from "@/Api/clients";
import { AddBtn } from "@/App/Clients/Add";
import { getSelectSellers } from "@/Api/sellers";
import { If } from "@/components/If";
import { usePermissions } from "@/hooks/usePermissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Users, Search, User, ChevronRight, Loader } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

export interface PaymentFormItem {
  payment_form_id: number;
  value: number;
  bank_account_id?: string;
  payment_advance_id?: number;
}

export interface ContestProductClassification {
  id: number;
  contest_id: number;
  lottery_id: number;
  product_name: string;
  product_classification_id: number;
  tenant_id: number;
  created_at: string;
  updated_at: string;
  product_classification: {
    id: number;
    name: string;
    sale_price: number;
  };
}

export interface SalesProductProps {
  id: number;
  unity_id: number;
  contest_id: number;
  lottery_id: number;
  cp_classification_id: number;
  cp_seller_balance: {
    id: number;
    seller_balance: number;
  };
  quota_quantity: number;
  requested_quantity?: number;
  marketplace_balance?: number;
  desk_balance?: number;
  other_unity_balances?: Array<{
    unity_id: number;
    name: string;
    balance: number;
  }>;
  seller_id: number;
  created_by: {
    id: number;
    name: string;
  };
  distribution_process_id: number;
  tenant_id: number;
  created_at: string;
  updated_at: string;
  contest_product_classification: {
    id: number;
    cp_unity_balance: {
      id: number;
      unity_balance: number;
    };
    contest_id: number;
    lottery_id: number;
    product_name: string;
    product_classification_id: number;
    tenant_id: number;
    created_at: string;
    updated_at: string;
    product_classification: {
      id: number;
      name: string;
      sale_price: number;
      lottery_id: number;
    };
    lottery: {
      id: number;
      name: string;
      color: string;
    };
    contest: {
      id: number;
      uuid: string;
      name: string;
      validation_date: string;
    };
  };
  distribution_process: {
    id: number;
    uuid: string;
    cp_classification_id: number;
    unity_id: number;
    quota_quantity: number;
    seller_id: number;
    distribution_type_id: number;
    distribution_process_status_id: number;
    created_by: number;
    created_at: string;
    updated_at: string;
  };
  contest: {
    id: number;
    uuid: string;
    name: string;
    validation_date: string;
  };
  seller: {
    id: number;
    uuid: string;
    phone: string;
    document: string;
    user_id: number;
    user: {
      id: number;
      uuid: string;
      name: string;
      email: string;
    };
  };
  unity: {
    id: number;
    name: string;
    seller_received_without_distribution?: boolean;
  };
  tenant: {
    id: number;
    name: string;
  };
}

interface SelectedClientInfo {
  id: string;
  name: string;
  uuid?: string;
  discount?: number;
  discount_type?: {
    id: number;
    name: string;
  };
  addresses?: Array<{
    id: number;
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
    zip_code: string;
    pivot: {
      client_id: number;
      address_id: number;
      is_main: number;
      reference: string | null;
      created_at: string;
      updated_at: string;
    };
  }>;
}

export const PageList = () => {
  const navigate = useNavigate();
  const steps = ["Produtos", "Cliente", "Entrega e Pagamento", "Confirmação"];
  const user = useAuth();
  const [currentStep, setCurrentStep] = useState(
    user?.user?.user_type_id == 2 ? -1 : 0
  );
  const [listData, setListData] = useState<SalesProductProps[]>([]);
  const [routes, setRoutes] = useState<Array<{ id: number; title: string }>>(
    []
  );
  const { hasPermission } = usePermissions();
  const [contestsSummary, setContestsSummary] = useState<SellerContestSummaryItem[]>([]);
  const [loadingContests, setLoadingContests] = useState<Set<number>>(new Set());
  const [sellers, setSellers] = useState<any>([]);
  const [sellerSearchTerm, setSellerSearchTerm] = useState<string>("");
  const [isLoadingSellerChange, setIsLoadingSellerChange] = useState<boolean>(false);
  const [selectedSeller, setSelectedSeller] = useState<{
    id: string;
    name: string;
    uuid?: string;
    unity_id?: string;
  }>(
    {} as {
      id: string;
      name: string;
      uuid?: string;
    }
  );
  const [clients, setClients] = useState<any>({
    current_page: 1,
    data: [],
    first_page_url: "",
    from: 1,
    last_page: 1,
    last_page_url: "",
    next_page_url: null,
    path: "",
    per_page: 15,
    prev_page_url: null,
    to: 1,
    total: 0,
  });
  const [selectedClient, setSelectedClient] =
    useState<SelectedClientInfo | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<
    Array<{ id: number; quantity: number }>
  >([]);
  const [deliveryMethods, setDeliveryMethods] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [paymentForms, setPaymentForms] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] =
    useState<number>(0);
  const [selectedPaymentForms, setSelectedPaymentForms] = useState<
    PaymentFormItem[]
  >([]);
  const [selectedRoute, setSelectedRoute] = useState<string>("");
  const [address, setAddress] = useState<any>({
    addressName: "",
    street: "",
    number: "",
    complement: "",
    reference: "",
    neighborhood: "",
    city: "",
    state: "",
    zip_code: "",
    latitude: "",
    longitude: "",
  });
  const [totalSaleAmount, setTotalSaleAmount] = useState<number>(0);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [isLead, setIsLead] = useState<boolean>(false);

  const DISCOUNT_TYPE = {
    IN_TARIFF_VALUE: 1,
    IN_SALE_VALUE: 2,
  } as const;

  // Busca todos os produtos de UM concurso específico do vendedor (chamado ao
  // expandir o concurso na tela de seleção de produtos). Caminha as páginas
  // até a página vir incompleta, sem depender de meta de paginação do backend
  // (que hoje não é retornada). Uma página com menos itens que o tamanho de
  // página do backend já indica que é a última — evita 1 requisição extra
  // vazia só pra confirmar o fim.
  const SELLER_BALANCES_PAGE_SIZE = 1000;

  const fetchContestProducts = async (contestId: number) => {
    setLoadingContests((prev) => new Set(prev).add(contestId));
    try {
      const collected: SalesProductProps[] = [];
      let page = 1;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const productsRes = await getSellerBalances(
          selectedSeller?.id || user?.user?.seller?.id || "",
          page,
          contestId
        );
        if (!productsRes.ok) break;

        const responseData = productsRes.data?.response;
        const pageItems: SalesProductProps[] = Array.isArray(responseData)
          ? responseData
          : Array.isArray(responseData?.data)
          ? responseData.data
          : [];

        if (pageItems.length === 0) break;

        collected.push(...pageItems);

        if (pageItems.length < SELLER_BALANCES_PAGE_SIZE) break;

        page += 1;
      }

      setListData((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        const newProducts = collected.filter((p) => !existingIds.has(p.id));
        return [...prev, ...newProducts];
      });
    } catch (error) {
      console.error("Error loading contest products:", error);
      toast.error("Erro ao carregar produtos do concurso");
    } finally {
      setLoadingContests((prev) => {
        const newSet = new Set(prev);
        newSet.delete(contestId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (user?.user?.user_type_id == 2 && !selectedSeller?.id) {
          const [deliveryRes, paymentRes, routesRes, sellersRes] =
            await Promise.all([
              getSelectSaleTypes(),
              getPaymentFormsForSale(),
              getCircuitRoutesData({
                start_date: Mask.getLocalDate(),
                end_date: Mask.getLocalDate(),
              }),
              getSelectSellers({ is_active: true }),
            ]);

          if (deliveryRes.ok)
            setDeliveryMethods(deliveryRes.data?.response || []);
          if (paymentRes.ok) setPaymentForms(paymentRes.data?.response || []);
          if (routesRes.ok) setRoutes(routesRes.data?.response || []);
          if (sellersRes.ok) setSellers(sellersRes.data?.response || []);
        } else {
          const [summaryRes, deliveryRes, paymentRes, routesRes, sellersRes] =
            await Promise.all([
              getSellerContestsSummary(selectedSeller?.id || user?.user?.seller?.id || ""),
              getSelectSaleTypes(),
              getPaymentFormsForSale(),
              getCircuitRoutesData({
                start_date: new Date(
                  Date.now() - new Date().getTimezoneOffset() * 60000
                )
                  .toISOString()
                  .split("T")[0],
                end_date: new Date().toISOString().split("T")[0],
              }),
              getSelectSellers({ is_active: true }),
            ]);

          setListData([]);
          setLoadingContests(new Set());
          if (summaryRes.ok) {
            setContestsSummary(summaryRes.data?.response || []);
          } else {
            setContestsSummary([]);
          }
          if (deliveryRes.ok)
            setDeliveryMethods(deliveryRes.data?.response || []);
          if (paymentRes.ok) setPaymentForms(paymentRes.data?.response || []);
          if (routesRes.ok) setRoutes(routesRes.data?.response || []);
          if (sellersRes.ok) setSellers(sellersRes.data?.response || []);

          if (isLoadingSellerChange) {
            setIsLoadingSellerChange(false);
            setCurrentStep(0);
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Erro ao carregar dados");
        setIsLoadingSellerChange(false);
      }
    })();
  }, [user?.user?.user_type_id, selectedSeller?.id]);

  useEffect(() => {
    let total = 0;
    let discount = 0;

    selectedProducts.forEach((selectedProduct) => {
      if (selectedProduct.quantity > 0) {
        const product = listData.find((p) => p.id === selectedProduct.id);
        if (product) {
          let price =
            product.contest_product_classification.product_classification
              ?.sale_price || 0;

          if (
            selectedClient?.discount_type?.id ===
              DISCOUNT_TYPE.IN_TARIFF_VALUE &&
            selectedClient?.discount &&
            selectedClient.discount > 0
          ) {
            const discountPerUnit = price * (selectedClient.discount / 100);
            discount += discountPerUnit * selectedProduct.quantity;
            price -= discountPerUnit;
          }

          total += price * selectedProduct.quantity;
        }
      }
    });

    if (
      selectedClient?.discount_type?.id === DISCOUNT_TYPE.IN_SALE_VALUE &&
      selectedClient?.discount &&
      selectedClient.discount > 0
    ) {
      discount = total * (selectedClient.discount / 100);
      total = Math.max(0, total - total * (selectedClient.discount / 100));
    }

    const roundedDiscount = Math.round(discount * 100) / 100;
    const roundedTotal = Math.round(total * 100) / 100;

    setDiscountAmount(roundedDiscount);
    setTotalSaleAmount(roundedTotal);
  }, [
    selectedProducts,
    listData,
    selectedClient,
    DISCOUNT_TYPE.IN_SALE_VALUE,
    DISCOUNT_TYPE.IN_TARIFF_VALUE,
  ]);
  const handleProductSelection = (productId: number, quantity: number) => {
    setSelectedProducts((prev) => {
      const existing = prev.find((p) => p.id === productId);
      if (existing) {
        return prev.map((p) => (p.id === productId ? { ...p, quantity } : p));
      }
      return [...prev, { id: productId, quantity }];
    });
  };

  const handlePaymentFormsChange = (forms: PaymentFormItem[]) => {
    setSelectedPaymentForms(forms);
  };

  /**
   * Calcula as distribuições necessárias da unidade para complementar o saldo do vendedor
   */
  const calculateRequiredDistributions = () => {
    const distributions: Array<{
      unity_id: number;
      contest_id: number;
      contest_product_classification_id: number;
      sellers: Array<{
        id: number;
        quota_quantity: number;
      }>;
    }> = [];

    const validProducts = selectedProducts.filter(
      (product) => product.quantity > 0
    );

    validProducts.forEach((selectedProduct) => {
      const product = listData.find((item) => item.id === selectedProduct.id);
      if (!product) return;

      const sellerBalance = product.cp_seller_balance?.seller_balance || 0;
      const requestedQuantity = selectedProduct.quantity;
      const canUseUnityBalance =
        product.unity?.seller_received_without_distribution === true;

      if (requestedQuantity > sellerBalance && canUseUnityBalance) {
        const neededFromUnity = requestedQuantity - sellerBalance;

        // Agrupa por contest_product_classification_id para evitar duplicatas
        const existingDistribution = distributions.find(
          (d) =>
            d.contest_product_classification_id === product.cp_classification_id
        );

        if (existingDistribution) {
          // Adiciona ao vendedor existente ou atualiza a quantidade
          const existingSeller = existingDistribution.sellers.find(
            (s) => s.id === Number(selectedSeller?.id || user?.user?.seller?.id)
          );

          if (existingSeller) {
            existingSeller.quota_quantity += neededFromUnity;
          } else {
            existingDistribution.sellers.push({
              id: Number(selectedSeller?.id || user?.user?.seller?.id),
              quota_quantity: neededFromUnity,
            });
          }
        } else {
          // Cria nova distribuição
          distributions.push({
            unity_id: Number(
              selectedSeller?.unity_id || user?.user?.seller?.unity_id
            ),
            contest_id: product.contest_id,
            contest_product_classification_id: product.cp_classification_id,
            sellers: [
              {
                id: Number(selectedSeller?.id || user?.user?.seller?.id),
                quota_quantity: neededFromUnity,
              },
            ],
          });
        }
      }
    });

    return distributions;
  };

  const handlePurchase = async () => {
    const validProducts = selectedProducts.filter(
      (product) => product.quantity > 0
    );

    if (
      !selectedClient ||
      validProducts.length === 0 ||
      !selectedDeliveryMethod ||
      selectedPaymentForms.length === 0
    ) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    const totalPaid = selectedPaymentForms.reduce(
      (sum, form) => sum + form.value,
      0
    );
    if (Math.abs(totalPaid - totalSaleAmount) > 0.01) {
      toast.error("O valor total pago deve ser igual ao valor total da venda");
      return;
    }

    if (
      (selectedDeliveryMethod === SALE_TYPE_ID.ENDOSSO ||
        selectedDeliveryMethod === SALE_TYPE_ID.SEDEX) &&
      selectedPaymentForms.some(
        (form) =>
          form.payment_form_id !== PAYMENT_FORMS.PIX &&
          form.payment_form_id !== PAYMENT_FORMS.PIX_BTG &&
          form.payment_form_id !== PAYMENT_FORMS.REINVESTMENT &&
          form.payment_form_id !== PAYMENT_FORMS.VALE &&
          form.payment_form_id !== PAYMENT_FORMS.PIX_MAQUINETA &&
          form.payment_form_id !== PAYMENT_FORMS.DEPOSIT &&
          form.payment_form_id !== PAYMENT_FORMS.PIX_CNPJ &&
          form.payment_form_id !== PAYMENT_FORMS.MONEY &&
          form.payment_form_id !== PAYMENT_FORMS.MONEY_BLEED &&
          form.payment_form_id !== PAYMENT_FORMS.ADVANCE_PAYMENT
      )
    ) {
      toast.error(
        "Este tipo de venda só permite pagamento via PIX, PIX Maquineta, Reinvestimento, Vale, Dinheiro, Pix Gerado ou Depósito"
      );
      return;
    }

    try {
      // Calcula e executa as distribuições necessárias
      const requiredDistributions = calculateRequiredDistributions();

      if (requiredDistributions.length > 0) {
        // Executa as distribuições em paralelo
        const distributionPromises = requiredDistributions.map((distribution) =>
          distributeDirectlyToSellers(distribution)
        );

        const distributionResults = await Promise.all(distributionPromises);

        // Verifica se todas as distribuições foram bem-sucedidas
        const failedDistributions = distributionResults.filter(
          (result) => !result.ok
        );

        if (failedDistributions.length > 0) {
          toast.error(
            "Erro ao distribuir produtos da unidade. Tente novamente."
          );
          return;
        }

        toast.success("Produtos distribuídos com sucesso!");
      }

      const purchaseData: SalesProps = {
        unity_id: selectedSeller?.unity_id || user?.user?.seller?.unity_id,
        client_id: isLead
          ? null
          : selectedClient
          ? Number(selectedClient.id)
          : null,
        seller_id: selectedSeller?.id || "",
        lead_id: isLead
          ? selectedClient
            ? Number(selectedClient.id)
            : null
          : null,
        sale_type_id: selectedDeliveryMethod,
        payment_forms: selectedPaymentForms.map((form) => ({
          payment_form_id: form.payment_form_id,
          value: Number(Number(form?.value).toFixed(2)),
          ...(form.payment_advance_id && { payment_advance_id: form.payment_advance_id }),
        })),
        address: {
          addressName: address?.addressName || "",
          street: address?.street || "",
          number: address?.number || "",
          complement: address?.complement || "",
          neighborhood: address?.neighborhood || "",
          city: address?.city || "",
          state: address?.state || "",
          zip_code: address?.zip_code || "",
          latitude: address?.latitude ? Number(address?.latitude) : "undefined",
          longitude: address?.longitude
            ? Number(address?.longitude)
            : "undefined",
        },
        circuit_route_id: selectedRoute || undefined,
        contest_product_classifications: validProducts.map((p) => {
          const product = listData.find((item) => item.id === p.id);
          return {
            id: product?.cp_classification_id || 0,
            quantity: p.quantity,
          };
        }),
      };

      const { ok } = await createSale(purchaseData);
      if (ok) {
        toast.success("Pedido realizado com sucesso!");
        navigate("/vendas");
      }
    } catch (error) {
      console.error("Erro ao realizar pedido:", error);
      toast.error("Erro ao realizar pedido");
    }
  };

  const updateClientsList = () => {
    const fetchClientsAndLeads = async () => {
      try {
        const response = await getSelectAllClientsAndLeads({
          seller_id: selectedSeller?.id || user?.user?.seller?.id || "",
        });
        if (response && response.data) {
          const data = response.data;

          if (data.status && data.response) {
            const clientsData = data.response.clients || [];
            const leadsData = data.response.leads || [];

            if (selectedClient) {
              const updatedClientInfo =
                clientsData.find((c) => String(c.id) === selectedClient.id) ||
                leadsData.find((l) => String(l.id) === selectedClient.id);

              if (updatedClientInfo) {
                setSelectedClient({
                  id: String(updatedClientInfo.id),
                  name: updatedClientInfo.name,
                  uuid: updatedClientInfo.uuid || "",
                  ...(updatedClientInfo || {}),
                });
              }
            }

            setClients({
              current_page: 1,
              data: [
                ...clientsData,
                ...(leadsData || []).map((lead) => ({
                  ...lead,
                  addresses: [],
                  isLead: true,
                })),
              ],
              first_page_url: "",
              from: 1,
              last_page: 1,
              last_page_url: "",
              next_page_url: null,
              path: "",
              per_page: clientsData.length,
              prev_page_url: null,
              to: clientsData.length,
              total: clientsData.length,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching clients and leads:", error);
      }
    };

    fetchClientsAndLeads();
  };

  useEffect(() => {
    if (
      selectedDeliveryMethod === SALE_TYPE_ID.ENDOSSO ||
      selectedDeliveryMethod === SALE_TYPE_ID.SEDEX
    ) {
      // Limpar as formas de pagamento existentes se mudou para um tipo especial
      if (
        selectedPaymentForms.some(
          (form) =>
            form.payment_form_id !== PAYMENT_FORMS.PIX &&
            form.payment_form_id !== PAYMENT_FORMS.PIX_BTG &&
            form.payment_form_id !== PAYMENT_FORMS.REINVESTMENT &&
            form.payment_form_id !== PAYMENT_FORMS.VALE &&
            form.payment_form_id !== PAYMENT_FORMS.PIX_MAQUINETA &&
            form.payment_form_id !== PAYMENT_FORMS.DEPOSIT
        )
      ) {
        setSelectedPaymentForms([]);
      }
    }
  }, [selectedDeliveryMethod]);

  const renderStep = () => {
    switch (currentStep) {
      case -1:
        const filteredSellers = sellers.filter((seller: any) =>
          seller?.user?.name?.toLowerCase().includes(sellerSearchTerm.toLowerCase())
        );

        return (
          <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="text-center space-y-2">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <h2 className="text-2xl font-semibold">Selecione um Vendedor</h2>
              <p className="text-gray-500">
                Escolha o vendedor para prosseguir com a venda
              </p>
            </div>

            <Card className="border-2">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Search className="w-5 h-5 text-gray-400" />
                  Buscar Vendedor
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Digite o nome do vendedor..."
                    value={sellerSearchTerm}
                    onChange={(e) => setSellerSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <ScrollArea className="h-[400px]">
                  {filteredSellers.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {filteredSellers.map((seller: any) => (
                        <Card
                          key={seller.id}
                          className="cursor-pointer transition-all border-2 hover:border-blue-400 hover:shadow-md hover:bg-blue-50"
                          onClick={() => {
                            setIsLoadingSellerChange(true);
                            setSelectedSeller({
                              id: String(seller.id),
                              name: seller?.user?.name,
                              unity_id: seller?.unity_id || "",
                              uuid: "",
                            });
                            setIsLead(false);
                            setSellerSearchTerm("");
                          }}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold">
                                {seller?.user?.name?.charAt(0)?.toUpperCase() || "V"}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-800">
                                  {seller?.user?.name}
                                </p>
                                {seller?.unity?.name && (
                                  <p className="text-sm text-gray-500">
                                    {seller.unity.name}
                                  </p>
                                )}
                              </div>
                              <ChevronRight className="w-5 h-5 text-gray-400" />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <User className="w-12 h-12 mb-3 text-gray-300" />
                      <p className="font-medium">Nenhum vendedor encontrado</p>
                      {sellerSearchTerm && (
                        <p className="text-sm">
                          Tente buscar por outro nome
                        </p>
                      )}
                    </div>
                  )}
                </ScrollArea>

                <div className="pt-2 border-t text-center text-sm text-gray-500">
                  {filteredSellers.length} vendedor{filteredSellers.length !== 1 ? "es" : ""} disponível{filteredSellers.length !== 1 ? "is" : ""}
                </div>
              </CardContent>
            </Card>
          </div>
        );
      case 0:
        return (
          <ProductSelection
            products={listData}
            selectedProducts={selectedProducts}
            onProductSelect={handleProductSelection}
            contestsSummary={contestsSummary}
            loadingContests={loadingContests}
            onExpandContest={fetchContestProducts}
          />
        );
      case 1:
        return (
          <ClientSelection
            clients={clients}
            sellerId={selectedSeller?.id || user?.user?.seller?.id || ""}
            selectedClient={selectedClient?.id || ""}
            address={address}
            onAddressChange={setAddress}
            onClientSelect={(
              clientId,
              isLead,
              name,
              uuid,
              discount,
              discountType
            ) => {
              setSelectedClient({
                id: clientId,
                name: name || "Cliente não selecionado",
                uuid: uuid || "",
                discount: discount,
                discount_type: discountType,
              });
              setIsLead(isLead || false);
            }}
            onNewClient={() => (
              <AddBtn updateFront={() => updateClientsList()} />
            )}
          />
        );
      case 2:
        return (
          <PaymentDeliverySelection
            deliveryMethods={deliveryMethods}
            paymentForms={paymentForms}
            selectedDeliveryMethod={selectedDeliveryMethod}
            routes={routes}
            selectedRoute={selectedRoute}
            onRouteSelect={setSelectedRoute}
            onDeliveryMethodSelect={(id) => {
              setSelectedDeliveryMethod(id);
            }}
            onAddressChange={setAddress}
            address={address}
            selectedPaymentForms={selectedPaymentForms}
            onPaymentFormsChange={handlePaymentFormsChange}
            totalSaleAmount={totalSaleAmount}
            discountAmount={discountAmount}
            selectedClientUuid={selectedClient?.uuid || ""}
            selectedClientId={selectedClient?.id ? Number(selectedClient.id) : undefined}
            selectedSellerId={selectedSeller?.id ? Number(selectedSeller.id) : user?.user?.seller?.id}
            selectedClientName={selectedClient?.name}
            selectedProducts={selectedProducts}
            products={listData}
          />
        );
      case 3:
        const clientAddressInfo = clients.data.find(
          (c) => c.id === Number(selectedClient?.id)
        );

        return (
          <OrderSummary
            selectedProducts={selectedProducts}
            products={listData}
            client={{
              id: Number(selectedClient?.id || 0),
              name: selectedClient?.name || "Cliente não selecionado",
              uuid: clientAddressInfo?.uuid || "",
              phone: clientAddressInfo?.phone || "",
              email: clientAddressInfo?.email || "",
              document: clientAddressInfo?.document,
              addresses: clientAddressInfo?.addresses || [],
              isLead: isLead,
              discount: selectedClient?.discount,
              discount_type: selectedClient?.discount_type,
              discount_type_id: selectedClient?.discount_type?.id,
            }}
            deliveryMethod={
              deliveryMethods.find((d) => d.id === selectedDeliveryMethod)
                ?.name || ""
            }
            paymentForms={selectedPaymentForms}
            paymentFormsData={paymentForms}
            address={address}
            onConfirm={handlePurchase}
            totalAmount={totalSaleAmount}
            discountAmount={discountAmount}
          />
        );
      default:
        return null;
    }
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 0:
        return selectedProducts.some((product) => product.quantity > 0);
      case 1:
        return !!selectedClient;
      case 2:
        const isSpecialType =
          selectedDeliveryMethod === SALE_TYPE_ID.ENDOSSO ||
          selectedDeliveryMethod === SALE_TYPE_ID.SEDEX;

        if (isSpecialType) {
          return !!selectedDeliveryMethod;
        }

        const totalPaid = selectedPaymentForms.reduce(
          (sum, form) => sum + form.value,
          0
        );
        return (
          !!selectedDeliveryMethod &&
          selectedPaymentForms.length > 0 &&
          Math.abs(totalPaid - totalSaleAmount) < 0.01
        );
      default:
        return true;
    }
  };

  return (
    <Main>
      <If condition={hasPermission("create-sales")}>
        {isLoadingSellerChange && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 flex flex-col items-center gap-4 shadow-xl">
              <Loader className="h-10 w-10 animate-spin text-blue-600" />
              <p className="text-gray-700 font-medium">Carregando produtos do vendedor...</p>
            </div>
          </div>
        )}
        <div className="container mx-auto">
          <Stepper steps={steps} currentStep={currentStep} />
          {renderStep()}

          <div className="flex justify-between mt-8">
            {(currentStep > 0 || (currentStep === 0 && user?.user?.user_type_id == 2)) && (
              <Button
                variant="outline"
                onClick={() => setCurrentStep((prev) => prev - 1)}
              >
                Voltar
              </Button>
            )}
            {currentStep >= 0 && currentStep < steps.length - 1 && (
              <Button
                onClick={() => setCurrentStep((prev) => prev + 1)}
                disabled={!canProceedToNextStep()}
                className="ml-auto"
              >
                Próximo
              </Button>
            )}
          </div>
        </div>
      </If>
    </Main>
  );
};
